import { MAX_MAILTO_RECIPIENTS, toBccMailto, toRecipientList } from './event-recipients';
import { AppEvent, EventRegistration } from './models';
import { findSubmissionForSlot, getRequiredListProgress } from './list-submission-progress';
import { getValidationIndicator, ListValidationIndicator } from './list-validation-indicator';
import { registrationDetailsComplete } from './registration-details';

export interface RegistrationSlotRow {
    slotId?: number;
    label: string;
    required: boolean;
    submitted: boolean;
    /** Signed off by an organiser. Mutually exclusive with `submitted`. */
    accepted: boolean;
    /** A list exists in this slot, whether or not it has been accepted yet. */
    present: boolean;
    /** The force's own name, so a slot row says which list its buttons act on. */
    listName?: string;
    /** Points cost from the submitted force's snapshot. */
    listPoints?: number;
    /** Faction KEY from the snapshot -- resolved to a name by the page, which has the library. */
    listFaction?: string;
    /** A text list carries no faction or points; shown as unknown rather than blank. */
    isTextList: boolean;
    /** The note an organiser left when accepting or rejecting. Shown so a co-organiser can
     *  see the reasoning -- particularly for a list accepted despite validation errors, or
     *  accepted with no list at all. */
    organiserNotes?: string;
    /** Resolved to a name by the page, which has the event's organiser details. */
    decidedByUserId?: string;
    decidedAt?: string;
    /** Accepted by the event's rule rather than by a person, so there is no name to show. */
    decidedAutomatically: boolean;
    /** Resubmitted after a rejection and awaiting another look. */
    resubmitted: boolean;
    /** Whether the stored validation summary actually reports errors. A text list has no
     *  summary at all, so this is false for one -- there is nothing to validate. */
    hasValidationErrors: boolean;
    indicator: ListValidationIndicator;
    /** Set only when there is a real force to open — never for text or unsubmitted lists. */
    viewableForceId?: string;
    /** Set only for a submitted text list — mutually exclusive with viewableForceId. */
    viewableText?: string;
}

export interface RegistrationRow {
    registration: EventRegistration;
    progress: { submitted: number; required: number };
    complete: boolean;
    /** Named against `complete` above, which means "all required lists submitted". Two
     *  similarly-named booleans on one interface is how the wrong one gets bound. */
    contactDetailsComplete: boolean;
    slots: RegistrationSlotRow[];
}

export interface GroupRecipients {
    addresses: string[];
    /**
     * A BCC mailto, or null when there are more addresses than a mail client will take. Clients
     * truncate an over-long mailto silently, so an organiser would mail some of their players and
     * have no way of knowing; past the limit the page offers copying instead.
     */
    mailto: string | null;
}

export interface RegistrationGroups {
    pending: RegistrationRow[];
    active: RegistrationRow[];
    waitlist: RegistrationRow[];
    /** Precomputed so the template binds to properties rather than calling a method per group. */
    recipients: {
        pending: GroupRecipients;
        active: GroupRecipients;
        waitlist: GroupRecipients;
        everyone: GroupRecipients;
    };
}

const noRecipients = (): GroupRecipients => ({ addresses: [], mailto: null });

const recipientsFor = (rows: RegistrationRow[]): GroupRecipients => {
    const addresses = toRecipientList(rows.map((r) => r.registration));
    return {
        addresses,
        mailto: addresses.length && addresses.length <= MAX_MAILTO_RECIPIENTS ? toBccMailto(addresses) : null
    };
};

/**
 * Builds the whole page in one pass, so the template binds to plain properties instead of calling
 * methods on every change-detection cycle (the page previously called three filter methods from
 * its template, once per group heading and again per list).
 */
export function buildRegistrationGroups(registrations: EventRegistration[] | undefined, event: AppEvent | undefined): RegistrationGroups {
    const groups: RegistrationGroups = {
        pending: [],
        active: [],
        waitlist: [],
        recipients: {
            pending: noRecipients(),
            active: noRecipients(),
            waitlist: noRecipients(),
            everyone: noRecipients()
        }
    };
    if (!event) {
        return groups;
    }

    for (const registration of registrations ?? []) {
        // Explicit rather than indexing groups by status: EventRegistrationStatus includes
        // 'withdrawn' and 'removed', which have no group, and indexing by the full union does not
        // type-check against RegistrationGroups.
        if (registration.status === 'pending') {
            groups.pending.push(buildRow(registration, event));
        } else if (registration.status === 'active') {
            groups.active.push(buildRow(registration, event));
        } else if (registration.status === 'waitlist') {
            groups.waitlist.push(buildRow(registration, event));
        }
        // withdrawn and removed registrations are not shown at all
    }

    groups.recipients = {
        pending: recipientsFor(groups.pending),
        active: recipientsFor(groups.active),
        waitlist: recipientsFor(groups.waitlist),
        everyone: recipientsFor([...groups.pending, ...groups.active, ...groups.waitlist])
    };

    return groups;
}

function buildRow(registration: EventRegistration, event: AppEvent): RegistrationRow {
    const progress = getRequiredListProgress(event.listSlots, registration.listSubmissions);
    const slots = (event.listSlots ?? []).map((slot) => {
        const submission = findSubmissionForSlot(registration.listSubmissions, slot);
        const submitted = submission?.status === 'submitted';
        const accepted = submission?.status === 'accepted';
        // Viewing and rejecting must both survive acceptance -- an organiser still needs to
        // read a list they signed off, and rejecting is how they hand it back.
        const present = submitted || accepted;
        return {
            slotId: slot.id,
            label: slot.label,
            required: !!slot.required,
            submitted,
            accepted,
            present,
            listName: submission?.snapshotData?.name,
            listPoints: submission?.snapshotData?.cost,
            listFaction: submission?.snapshotData?.faction,
            isTextList: submission?.submissionKind === 'text',
            organiserNotes: submission?.toNotes,
            decidedByUserId: submission?.decidedByUserId,
            decidedAt: submission?.decidedAt,
            decidedAutomatically: !!submission?.decidedAutomatically,
            resubmitted: !!submission?.resubmittedAfterRejection,
            hasValidationErrors: (submission?.validationSummary?.errors ?? 0) > 0,
            indicator: getValidationIndicator(submission),
            viewableForceId: present && submission.submissionKind !== 'text' ? submission.forceId : undefined,
            viewableText: present && submission.submissionKind === 'text' ? submission.textSubmission : undefined
        };
    });

    return {
        registration,
        progress,
        complete: progress.submitted === progress.required,
        contactDetailsComplete: registrationDetailsComplete(registration),
        slots
    };
}
